import React, { useState, useRef } from 'react';
import styles from './A_Input.module.css';
import cn from 'classnames';
import { Q_Icon } from '@/components/Q_Quarks/Q_Icon/Q_Icon';

interface A_InputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'email' | 'password' | 'text' | 'phone';
  required?: boolean;
  disabled?: boolean;
  error?: string;
  maxLength?: number;
  borderRadius?: 'inputs' | 'text-fields';
  background?: 'white' | 'gray';
  maxWidth?: string;
}

export default function A_Input({
  label,
  value,
  onChange,
  type,
  required = false,
  disabled = false,
  error,
  maxLength,
  borderRadius = 'inputs',
  background = 'white',
  maxWidth = '612',
}: A_InputProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const randomName = `field_${Math.random().toString(36).substr(2, 9)}`;

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;

    if (type === 'phone') {
      const digits = newValue.replace(/\D/g, '');

      const limitedDigits = digits.slice(0, 11);

      if (limitedDigits.length === 0) {
        newValue = '';
      } else if (limitedDigits.length <= 1) {
        newValue = `+${limitedDigits}`;
      } else if (limitedDigits.length <= 4) {
        newValue = `+${limitedDigits.slice(0, 1)} (${limitedDigits.slice(1)}`;
      } else if (limitedDigits.length <= 7) {
        newValue = `+${limitedDigits.slice(0, 1)} (${limitedDigits.slice(1, 4)}) ${limitedDigits.slice(4)}`;
      } else if (limitedDigits.length <= 9) {
        newValue = `+${limitedDigits.slice(0, 1)} (${limitedDigits.slice(1, 4)}) ${limitedDigits.slice(4, 7)} ${limitedDigits.slice(7)}`;
      } else {
        newValue = `+${limitedDigits.slice(0, 1)} (${limitedDigits.slice(1, 4)}) ${limitedDigits.slice(4, 7)} ${limitedDigits.slice(7, 9)}-${limitedDigits.slice(9)}`;
      }

      onChange(newValue);
    } else {
      if (maxLength && newValue.length <= maxLength) {
        onChange(newValue);
      } else if (!maxLength) {
        onChange(newValue);
      }
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const isActive = isFocused || value.length > 0;
  const isPassword = type === 'password';
  const inputType =
    isPassword && showPassword ? 'text' : type === 'phone' ? 'tel' : type;

  return (
    <div className={styles.wrapper}>
      <div
        className={cn(
          styles.input_wrapper,
          styles[`border-radius-${borderRadius}`],
          styles[`background-${background}`],
          styles[`max-width-${maxWidth}`]
        )}
      >
        <div className={styles.input_container}>
          <label
            className={cn(
              styles.label,
              isActive && styles.label_active,
              isActive ? 'text-p-8' : 'text-p-2'
            )}
            onClick={() => inputRef.current?.focus()}
          >
            {label}
          </label>
          <input
            ref={inputRef}
            type={inputType}
            name={randomName}
            value={value}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
            required={required}
            maxLength={maxLength}
            className={cn(
              styles.input,
              'text-p-2',
              error && styles.input_error
            )}
            autoComplete='new-password'
            autoCorrect='off'
            autoCapitalize='off'
            spellCheck='false'
            data-lpignore='true'
            data-form-type='other'
            data-1p-ignore
            data-bwignore
            placeholder=' '
          />

          {maxLength && (
            <span className={cn(styles.char_count, 'text-caption')}>
              {value.length}/{maxLength}
            </span>
          )}
        </div>

        {isPassword && (
          <button
            type='button'
            className={styles.password_toggle}
            onClick={togglePasswordVisibility}
            tabIndex={-1}
          >
            {showPassword ? (
              <Q_Icon.PasswordCheckNoIcon width={20} height={20} fill='none' />
            ) : (
              <Q_Icon.PasswordCheckIcon width={20} height={20} fill='none' />
            )}
          </button>
        )}
      </div>
      {error && (
        <span className={cn(styles.error, 'text-caption')}>{error}</span>
      )}
    </div>
  );
}
